import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, AlertCircle, Loader2 } from "lucide-react";

export default function OrderSuccessPage() {
  const [state, setState] = useState({ phase: "checking", payment_status: null, attempts: 0 });
  const search = new URLSearchParams(useLocation().search);
  const sessionId = search.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session id" });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    async function poll() {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({
            phase: "paid",
            payment_status: "paid",
            amount: data.amount_total,
            currency: data.currency,
          });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired" });
          return;
        }
        if (attempts >= 8) {
          setState({ phase: "pending" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) {
          setState({ phase: "error", message: formatApiError(e.response?.data?.detail) || e.message });
        }
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="order-success-page"
      className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[#E8E5DF] p-10 text-center">
        {state.phase === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#C05746] mx-auto" strokeWidth={1.5} />
            <p className="mt-6 font-serif-display text-3xl">Confirming payment…</p>
            <p className="mt-2 text-[#5C625E]">This usually takes a few seconds.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <div className="h-16 w-16 rounded-full bg-[#5A7247]/10 text-[#5A7247] flex items-center justify-center mx-auto">
              <Check className="h-8 w-8" strokeWidth={1.5} />
            </div>
            <p className="mt-6 font-serif-display text-4xl">Grazie!</p>
            <p className="mt-2 text-[#5C625E]">
              Payment received{" "}
              {state.amount
                ? `(${money((state.amount || 0) / 100)} ${(state.currency || "").toUpperCase()})`
                : ""}
              . We'll have your order ready at pickup.
            </p>
          </>
        )}
        {(state.phase === "pending" || state.phase === "expired" || state.phase === "error") && (
          <>
            <div className="h-16 w-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
              <AlertCircle className="h-8 w-8" strokeWidth={1.5} />
            </div>
            <p className="mt-6 font-serif-display text-3xl">
              {state.phase === "expired" ? "Session expired" : "Still processing"}
            </p>
            <p className="mt-2 text-[#5C625E]">
              {state.message ||
                "Payment is still being verified. Check your email for a confirmation."}
            </p>
          </>
        )}
        <Link to="/menu" className="inline-block mt-8">
          <Button
            data-testid="order-success-menu-button"
            className="bg-[#121614] text-white hover:bg-black rounded-full px-8"
          >
            Back to menu
          </Button>
        </Link>
      </div>
    </div>
  );
}
